import { useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { ConstantsLagoon } from '../common/ConstantsLagoon';
import { isIfpMember } from '../common/Utils/memberUtils/memberUtils';
import { Provider } from '../models/Provider';
import { ProviderLocation } from '../models/ProviderDetails';
import { useGuestAcquisitionStore } from '../store/useGuestAcquisitionStore';
import { GuestAcquisitionState } from '../store/useGuestAcquisitionStore/guestAcquisitionStore';
import { usePCPStore } from '../store/usePCPStore';
import { PCPSearchStore } from '../store/usePCPStore/pcpStore';
import {
  getDetailsNormalizedProviderId,
  getProviderPCPId,
  isCurrentPcpProvider,
} from '../utils/providerDetails.utils';
import { getCurrentMember, getCurrentMemberPCPInfo } from '../utils/user.utils';
import { useFeatureFlag } from './useFeatureFlag';
import { usePrimaryCare } from './usePrimaryCare';

type UseCurrentPcpBadgeParams = {
  choosePCP?: boolean;
  coverageType?: string;
  practitioner?: Provider;
  providerId?: string;
};

/**
 * Derives whether the current provider result is the member's active PCP and,
 * from that, whether to show the green "PCP" badge (and suppress the Choose PCP
 * button). Shared by DataCard and GroupedPinDataCard.
 */
export const useCurrentPcpBadge = ({
  choosePCP = false,
  coverageType,
  practitioner,
  providerId,
}: UseCurrentPcpBadgeParams) => {
  const [
    enableSuppressChoosePcpForCurrentPcp,
    enableAllStaffModelEni,
    enableAllStaffModelMnr,
  ] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_SUPPRESS_CHOOSE_PCP_FOR_CURRENT_PCP,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_ALL_STAFF_MODEL_ENI,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_ALL_STAFF_MODEL_MNR,
  ]);

  const currentMember = getCurrentMember();

  const { dependentSeqNbr } = usePCPStore(
    useShallow((state: PCPSearchStore) => ({
      dependentSeqNbr: state.pcpSearchState.dependentSeqNbr,
    }))
  );

  const { isNavigatingMaestro } = useGuestAcquisitionStore(
    useShallow((state: GuestAcquisitionState) => ({
      isNavigatingMaestro: state.isNavigatingMaestro,
    }))
  );

  const { data: primaryCareData } = usePrimaryCare();

  const currentPcpInfo = getCurrentMemberPCPInfo(
    dependentSeqNbr || currentMember?.demographics?.dependentSeqNbr,
    primaryCareData?.primaryCare
  );

  const normalizedProviderIdForPcp = getDetailsNormalizedProviderId(
    coverageType,
    currentMember?.lineOfBusiness,
    providerId
  );
  const providerPCPIdValue = getProviderPCPId(
    [{ pcpId: practitioner?.pcpId }] as ProviderLocation[],
    providerId,
    currentPcpInfo?.providerID
  );
  const providerPCPId = isIfpMember(currentMember?.lineOfBusiness)
    ? providerPCPIdValue
    : getDetailsNormalizedProviderId(
        coverageType,
        currentMember?.lineOfBusiness,
        providerPCPIdValue
      );
  const primaryCarePCPId = getDetailsNormalizedProviderId(
    coverageType,
    currentMember?.lineOfBusiness,
    currentPcpInfo?.pcpID
  );
  const primaryCareProviderPCPId = getDetailsNormalizedProviderId(
    coverageType,
    currentMember?.lineOfBusiness,
    currentPcpInfo?.providerID
  );
  const isStaffModelPcp =
    (enableAllStaffModelEni || enableAllStaffModelMnr) &&
    currentPcpInfo?.isStaffModel;
  const isCurrentPcpProviderResult = useMemo(
    () =>
      isCurrentPcpProvider(
        normalizedProviderIdForPcp,
        providerPCPId,
        primaryCareProviderPCPId,
        primaryCarePCPId,
        currentPcpInfo?.providerIds
      ),
    [
      normalizedProviderIdForPcp,
      providerPCPId,
      primaryCareProviderPCPId,
      primaryCarePCPId,
      currentPcpInfo?.providerIds,
    ]
  );
  const hideChoosePcpForCurrentPcp = useMemo(
    () =>
      enableSuppressChoosePcpForCurrentPcp &&
      !isStaffModelPcp &&
      isCurrentPcpProviderResult,
    [
      enableSuppressChoosePcpForCurrentPcp,
      isStaffModelPcp,
      isCurrentPcpProviderResult,
    ]
  );

  const showPcpBadge =
    choosePCP && hideChoosePcpForCurrentPcp && !isNavigatingMaestro;

  return { showPcpBadge, hideChoosePcpForCurrentPcp };
};
